// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * HR Match Debug card (global admin only).
 *
 * Dry-runs the production HR matching path on typed-in identity fields — no
 * document, no DynamoDB write, no Optix write-back. Only the i9processor Lambda
 * can reach the HR Oracle databases, so "Run" publishes an i9_match_debug job
 * (same path as the schema debug); the Lambda writes a report to S3, which this
 * card polls and renders. Useful in dev, where the test documents carry fake PII:
 * type a real employee's fields, mangled the way OCR would mangle them, and see
 * which matching tier catches each variation.
 */
import { useState } from 'react'
import type { FC } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Input,
} from '@/components/ui'
import { FlaskConical, Play, Loader2 } from 'lucide-react'
import { useServices } from '@/app-shell/services'
import { statusColor } from './StatusBadge'
import { formatTimestamp } from './format'

interface MatchDebugReport {
  available: boolean
  jobId: string | null
  generatedAt: string | null
  ocr: Record<string, string>
  hrMatch: Record<string, unknown> | null
  optixMetadata: Record<string, string> | null
  error: string | null
}

const FIELD_MATCH_FLAGS = [
  { key: 'ssn_match', label: 'SSN' },
  { key: 'first_name_match', label: 'First name' },
  { key: 'last_name_match', label: 'Last name' },
  { key: 'dob_match', label: 'DOB' },
] as const

interface MatchDebugCardProps {
  orgId: string
  workspaceId: string
}

export const MatchDebugCard: FC<MatchDebugCardProps> = ({ orgId, workspaceId }) => {
  const services = useServices()
  const apiClient = services.getClient()
  const basePath = `/org/${orgId}/ws/${workspaceId}/i9`

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [socialSecurityNumber, setSocialSecurityNumber] = useState('')

  // Job id of the run queued from this card; while a fresh report hasn't landed,
  // the GET is polled and the button shows as running.
  const [queuedJobId, setQueuedJobId] = useState<string | null>(null)

  const { data: report } = useQuery({
    queryKey: ['i9', 'match-debug', orgId, workspaceId],
    refetchInterval: queuedJobId ? 3000 : false,
    queryFn: async (): Promise<MatchDebugReport> => {
      const response = await apiClient.get<MatchDebugReport>(`${basePath}/match-debug`)
      const data = response.data
      // The queued run has landed once the stored report carries its job id.
      if (queuedJobId && data.jobId === queuedJobId) setQueuedJobId(null)
      return data
    },
  })

  const runMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      const response = await apiClient.post<{ jobId: string }>(`${basePath}/match-debug`, {
        firstName,
        lastName,
        dateOfBirth,
        socialSecurityNumber,
      })
      return response.data.jobId
    },
    onSuccess: (jobId) => setQueuedJobId(jobId),
  })

  const isRunning = runMutation.isPending || queuedJobId !== null
  const hrMatch = report?.hrMatch ?? null
  const bucket = typeof hrMatch?.bucket === 'string' ? hrMatch.bucket : null
  // The processor buckets are matched/partial_match/unmatched; unmatched has no
  // status color of its own, so it borrows no_match's.
  const bucketColor = statusColor(bucket === 'unmatched' ? 'no_match' : (bucket ?? ''))

  return (
    <Card data-testid="i9-match-debug">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FlaskConical className="h-5 w-5" />
              HR Match Debug
            </CardTitle>
            <CardDescription>
              Dry-run the HR matching on typed-in fields — no document, no Optix write.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => runMutation.mutate()}
            disabled={isRunning || !(firstName.trim() || lastName.trim() || socialSecurityNumber.trim())}
          >
            {isRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
            {isRunning ? 'Running…' : 'Run match'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" aria-label="First name" />
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" aria-label="Last name" />
          <Input value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} placeholder="Date of birth (any format)" aria-label="Date of birth" />
          <Input value={socialSecurityNumber} onChange={(e) => setSocialSecurityNumber(e.target.value)} placeholder="SSN" aria-label="SSN" />
        </div>

        {runMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            Failed to queue match run: {(runMutation.error as Error).message}
          </div>
        )}

        {!report?.available && !isRunning && (
          <p className="text-sm text-muted-foreground">
            No report yet. Enter identity fields and run a match.
          </p>
        )}

        {report?.available && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {bucket && (
                <span className={`px-2 py-1 rounded text-xs ${bucketColor}`}>{bucket}</span>
              )}
              {typeof hrMatch?.match_percent === 'number' && (
                <Badge variant="secondary">{hrMatch.match_percent}% match</Badge>
              )}
              {hrMatch?.multiple_records === true && (
                <Badge variant="destructive">multiple records</Badge>
              )}
              {typeof hrMatch?.uid === 'string' && hrMatch.uid && (
                <span className="font-mono text-muted-foreground">UID {hrMatch.uid}</span>
              )}
              <span className="text-muted-foreground">Last run: {formatTimestamp(report.generatedAt)}</span>
            </div>

            {report.error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                HR lookup failed: {report.error}
              </div>
            )}

            {hrMatch && (
              <div className="flex flex-wrap gap-2">
                {FIELD_MATCH_FLAGS.map(({ key, label }) => (
                  <Badge key={key} variant={hrMatch[key] === true ? 'default' : 'outline'}>
                    {label}: {hrMatch[key] === true ? 'match' : 'no match'}
                  </Badge>
                ))}
              </div>
            )}

            {hrMatch && (
              <p className="text-sm text-muted-foreground">
                Name as it would be recorded:{' '}
                <span className="font-mono">
                  {report.ocr.last_name || '—'}, {report.ocr.first_name || '—'}
                </span>
              </p>
            )}

            {report.optixMetadata && Object.keys(report.optixMetadata).length > 0 && (
              <div className="text-sm">
                <p className="mb-1 font-medium">Would write to Optix</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(report.optixMetadata).map(([field, value]) => (
                    <Badge key={field} variant="secondary" className="font-mono">
                      {field}={value}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {report.optixMetadata && Object.keys(report.optixMetadata).length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing would be written to Optix.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
